import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { ProfilePhotoApi } from '@/core/user/profile-photo-api.service';
import type { User } from '@/core/user/user.model';
import { AuthStore } from '@/features/auth-store.service';

@Injectable({ providedIn: 'root' })
export class ProfilePhotoStore {
  private readonly profilePhotoApi = inject(ProfilePhotoApi);
  private readonly authStore = inject(AuthStore);

  private readonly _photo = signal<string | undefined>(undefined);
  private requestedForUserId: string | undefined;

  public readonly photo = this._photo.asReadonly();

  constructor() {
    effect(() => {
      const user = this.authStore.currentUser();
      if (user === undefined) {
        this.clear();
        return;
      }
      if (!this.authStore.hasPermission('GET_OWN_PROFILE_PHOTO')) return;
      untracked(() => void this.loadOwnPhoto(user));
    });
  }

  private async loadOwnPhoto(user: User): Promise<void> {
    if (user.id === this.requestedForUserId) return;

    this.requestedForUserId = user.id;
    try {
      const photo = await this.profilePhotoApi.getOwnProfilePhoto(user.id);
      this.setPhotoIfStillConnected(user.id, photo);
    } catch {
      this.allowRetryOnNextUserChange(user.id);
    }
  }

  private setPhotoIfStillConnected(userId: string, photo: string | undefined): void {
    if (this.authStore.currentUser()?.id === userId) this._photo.set(photo);
  }

  private allowRetryOnNextUserChange(userId: string): void {
    if (this.requestedForUserId === userId) this.requestedForUserId = undefined;
  }

  private clear(): void {
    this.requestedForUserId = undefined;
    this._photo.set(undefined);
  }
}
